/* ================================================================
   NY2K FABLE MOTION SYSTEM — GSAP + ScrollTrigger + Lenis
   Source of truth: NY2K Motion Spec Cheat Sheet (locked values).
   Theme-editor safe: sections re-init on shopify:section:load.
================================================================ */

/* ---- animation tokens (single source, no magic numbers) ---- */
const FBL_EASE = { reveal:"power3.out", hero:"expo.out", drift:"none",
  magnetic:"power2.out", exit:"power2.in" };
const FBL_DUR = { micro:0.4, base:0.9, slow:1.2, cinema:1.4 };
const FBL_STAGGER = { tight:0.06, base:0.09, loose:0.14 };

/* Text reveals play once and stay put. Reversing them on scroll-up made
   whole sections flicker out under Lenis momentum on a fast upward flick.
   Set to false to restore the old reverse behaviour — that single flag is
   the entire revert. */
const FBL_REVEAL_ONCE = true;
const FBL_ST = {
  start: "top 82%",
  toggle: FBL_REVEAL_ONCE ? "play none none none" : "play none none reverse",
};

/* Breathing room below the sticky header when an in-page anchor lands. */
const FBL_ANCHOR_PAD = 16;

(function () {
  "use strict";
  if (!window.gsap || !window.ScrollTrigger) return;
  if (window.__fblInit) return;           // double-load guard
  window.__fblInit = true;
  gsap.registerPlugin(ScrollTrigger);

  /* Mobile browser chrome showing/hiding changes viewport height. Without
     this, every one of those changes refreshes all triggers mid-scroll and
     pinned/scrubbed sections visibly jump. */
  ScrollTrigger.config({ ignoreMobileResize: true });

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isMobile = window.matchMedia("(max-width: 959px)").matches;

  if (reduce) {
    gsap.set(".fbl-process-step", { opacity:1, y:0 });
    gsap.set("[data-fbl-progress], .fbl-process-line", { scaleY:1 });
    return; // static final states, zero motion
  }

  /* ---- Lenis smooth scroll (momentum per spec) ---- */
  if (window.Lenis && !window.__fblLenis) {
    const lenis = new Lenis({
      duration: isMobile ? 0.9 : 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true, touchMultiplier: 1.5,
    });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    window.__fblLenis = lenis;
  }

  /* Sticky header height, measured rather than assumed — the NY2K header
     rests near 90px and shrinks 68px on scroll, so a hardcoded offset drops
     the anchor target underneath it. Re-measured on every anchor click so
     this stays correct after any header or section-height change. */
  function headerOffset() {
    const root = document.querySelector("#shopify-section-ny2k-header")
      || document.querySelector("header");
    let h = root ? root.getBoundingClientRect().height : 0;
    if (h < 20 && root) {
      const inner = root.querySelector("*");
      if (inner) h = inner.getBoundingClientRect().height;
    }
    if (!h || h < 20) h = 90;
    return -(h + FBL_ANCHOR_PAD);
  }

  /* In-page anchors glide on Lenis instead of native jump */
  document.addEventListener("click", (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a || !window.__fblLenis) return;
    const href = a.getAttribute("href");
    if (!href || href.length < 2) return;
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();
    window.__fblLenis.scrollTo(target, { offset: headerOffset() });
  });

  /* ---- per-section init, re-runnable for the theme editor ---- */
  function killWithin(root) {
    ScrollTrigger.getAll().forEach((st) => {
      /* kill(true) REVERTS. Without the flag, a pinned trigger leaves its
         pin-spacer element behind, so every reload of the credential section
         in the theme editor stacked another spacer's worth of dead height. */
      if (st.trigger && root.contains(st.trigger)) st.kill(true);
    });
  }

  function initHero(root) {
    const media = root.querySelector(".fbl-hero-media");
    if (!media) return;
    const tl = gsap.timeline({ defaults: { ease: FBL_EASE.hero } });
    tl.from(media, { scale: 1.08, duration: FBL_DUR.cinema })
      /* 110 not 100: .fbl-line-mask carries padding-bottom for descenders, so
         the mask box is taller than the line. Moving the line exactly its own
         height left a sliver of glyph tops showing at the start of each
         reveal. */
      .from(root.querySelectorAll(".fbl-hero-line"), { yPercent: 110, opacity: 0,
        duration: FBL_DUR.cinema, stagger: FBL_STAGGER.loose, ease: "expo.out" }, "-=1.0")
      .from(root.querySelectorAll("[data-fbl-hero-fade]"), { y: 20, opacity: 0,
        duration: FBL_DUR.base, ease: FBL_EASE.reveal, stagger: FBL_STAGGER.base }, "-=0.6");
    gsap.to(media, { yPercent: 12, ease: FBL_EASE.drift,
      scrollTrigger: { trigger: root, start: "top top", end: "bottom top", scrub: true } });
  }

  function initReveals(root) {
    root.querySelectorAll(".fbl-reveal").forEach((el) => {
      const items = el.querySelectorAll(".fbl-reveal-item");
      if (!items.length) return;
      const tall = el.classList.contains("fbl-statement");
      gsap.from(items, { y: tall ? 40 : 28, opacity: 0,
        duration: tall ? FBL_DUR.slow : FBL_DUR.base,
        ease: FBL_EASE.reveal, stagger: FBL_STAGGER.base,
        scrollTrigger: { trigger: el, start: FBL_ST.start,
          toggleActions: FBL_ST.toggle, once: FBL_REVEAL_ONCE } });
    });
    root.querySelectorAll(".fbl-mask").forEach((el) => {
      const inner = el.querySelector(".fbl-plate, .fbl-plate-img, img, video");
      const hasParallax = el.hasAttribute("data-fbl-parallax");
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: "top 85%",
        toggleActions: FBL_ST.toggle, once: FBL_REVEAL_ONCE } });
      tl.fromTo(el, { clipPath: "inset(0 0 100% 0)" },
        { clipPath: "inset(0 0 0% 0)", duration: FBL_DUR.slow, ease: FBL_EASE.hero });
      /* Skip the scale-in wherever a parallax scrub already owns this
         element's transform. Two tweens writing one matrix — one scrubbed,
         one not — produced a visible settle as the reveal finished.
         Affects .fbl-prov-a, .fbl-prov-b and .fbl-statement-media. */
      if (inner && !hasParallax) {
        tl.from(inner, { scale: 1.2, duration: FBL_DUR.slow, ease: FBL_EASE.hero }, 0);
      }
    });
    if (!isMobile) {
      root.querySelectorAll("[data-fbl-parallax] .fbl-plate, [data-fbl-parallax] .fbl-plate-img")
        .forEach((img) => {
          gsap.to(img, { yPercent: -8, ease: FBL_EASE.drift,
            scrollTrigger: { trigger: img, start: "top bottom", end: "bottom top", scrub: true } });
        });
    }
  }

  function initCredential(root) {
    const sec = root.classList.contains("fbl-credential") ? root
      : root.querySelector(".fbl-credential");
    if (!sec) return;
    const lines = sec.querySelectorAll(".fbl-credential-copy .fbl-line");
    if (!isMobile) {
      /* scrub 0.5, not 1. Lenis already applies ~1.1s of smoothing; a full
         second of scrub on top compounded into noticeable lag behind the
         wheel on the one pinned section of the page. */
      const pin = gsap.timeline({ scrollTrigger: { trigger: sec, start: "top top",
        end: "+=120%", pin: true, scrub: 0.5, anticipatePin: 1 } });
      pin.from(sec.querySelector(".fbl-credential-badge"),
            { scale: 0.85, opacity: 0, ease: FBL_EASE.drift })
         .from(lines, { yPercent: 110, opacity: 0, stagger: 0.2, ease: FBL_EASE.drift }, "<0.2")
         .from(sec.querySelectorAll(".fbl-credential-body, .fbl-credential-link"),
            { opacity: 0, y: 24, stagger: 0.2, ease: FBL_EASE.drift }, "<0.4")
         .to(sec.querySelector("[data-fbl-progress]"), { scaleY: 1, ease: FBL_EASE.drift }, 0);
    } else {
      gsap.from(sec.querySelector(".fbl-credential-badge"), { opacity: 0, y: 28,
        duration: FBL_DUR.slow, ease: FBL_EASE.reveal,
        scrollTrigger: { trigger: sec, start: FBL_ST.start,
          toggleActions: FBL_ST.toggle, once: FBL_REVEAL_ONCE } });
      gsap.from([...lines, ...sec.querySelectorAll(".fbl-credential-body, .fbl-credential-link")],
        { y: 28, opacity: 0, duration: FBL_DUR.base, ease: FBL_EASE.reveal,
          stagger: FBL_STAGGER.base,
          scrollTrigger: { trigger: sec.querySelector(".fbl-credential-copy"),
            start: FBL_ST.start, toggleActions: FBL_ST.toggle, once: FBL_REVEAL_ONCE } });
    }
  }

  /* PROCESS — steps reveal on enter; only the rail line is scrubbed. */
  function initProcess(root) {
    const sec = root.classList.contains("fbl-process") ? root
      : root.querySelector(".fbl-process");
    if (!sec) return;
    const steps = sec.querySelectorAll(".fbl-process-step");
    if (steps.length) {
      gsap.set(steps, { opacity: 0, y: 28 });
      ScrollTrigger.batch(steps, {
        start: "top 88%",
        onEnter: (b) => gsap.to(b, { opacity: 1, y: 0, duration: FBL_DUR.base,
          ease: FBL_EASE.reveal, stagger: FBL_STAGGER.base, overwrite: true }),
        onLeaveBack: FBL_REVEAL_ONCE ? null : (b) => gsap.to(b, { opacity: 0, y: 28,
          duration: FBL_DUR.micro, ease: FBL_EASE.exit, overwrite: true }),
      });
    }
    const line = sec.querySelector(".fbl-process-line");
    if (line) gsap.fromTo(line, { scaleY: 0 },
      { scaleY: 1, transformOrigin: "top", ease: FBL_EASE.drift,
        scrollTrigger: { trigger: sec, start: "top 70%", end: "bottom 80%", scrub: 0.8 } });
  }

  /* MARQUEE — the ribbon is a CSS keyframe loop. Left alone it composites
     twelve frames continuously for the whole session, on screen or not.
     Pause it out of view. Uses a class, not an inline style, so the CSS
     hover-pause rule still wins when the pointer is over it. */
  function initMarquee(root) {
    if (!("IntersectionObserver" in window)) return;
    root.querySelectorAll(".fbl-marquee-track").forEach((track) => {
      if (track.__fblIO) return;
      track.classList.add("is-paused");
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => track.classList.toggle("is-paused", !e.isIntersecting));
      }, { rootMargin: "200px 0px" });
      io.observe(track);
      track.__fblIO = io;
    });
  }

  function initMagnetic(root) {
    if (isMobile || !matchMedia("(hover:hover)").matches) return;
    root.querySelectorAll(".fbl-magnetic").forEach((btn) => {
      if (btn.__fblMag) return;
      btn.__fblMag = true;
      btn.addEventListener("mousemove", (e) => {
        const r = btn.getBoundingClientRect();
        gsap.to(btn, { x: (e.clientX - r.left - r.width / 2) * 0.3,
          y: (e.clientY - r.top - r.height / 2) * 0.3,
          duration: FBL_DUR.micro, ease: FBL_EASE.magnetic });
      });
      btn.addEventListener("mouseleave", () =>
        gsap.to(btn, { x: 0, y: 0, duration: FBL_DUR.base, ease: "elastic.out(1,0.4)" }));
    });
  }

  function initSection(root) {
    killWithin(root);
    if (root.classList.contains("fbl-hero") || root.querySelector(".fbl-hero")) {
      initHero(root.classList.contains("fbl-hero") ? root : root.querySelector(".fbl-hero"));
    }
    initReveals(root); initCredential(root); initProcess(root);
    initMarquee(root); initMagnetic(root);
  }

  function initAll() {
    document.querySelectorAll(".fbl-hero").forEach(initHero);
    initReveals(document); initCredential(document);
    initProcess(document); initMarquee(document); initMagnetic(document);
    ScrollTrigger.refresh();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else { initAll(); }

  /* Trigger positions are measured at init, before webfonts swap and before
     media reports its size. Cormorant swapping in changes the height of every
     heading, which shifts every start position below it — so first-load
     reveals fired early and only corrected themselves on the next resize. */
  window.addEventListener("load", () => ScrollTrigger.refresh());
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }

  /* ---- THEME EDITOR GUARD: re-init reloaded section + refresh ---- */
  document.addEventListener("shopify:section:load", (e) => {
    if (e.target.querySelector('[class*="fbl-"]')) initSection(e.target);
    ScrollTrigger.refresh();
  });
  document.addEventListener("shopify:section:unload", () => ScrollTrigger.refresh());
})();
